import { faBuildingColumns } from '@fortawesome/free-solid-svg-icons'
import { format } from 'date-fns'

import { Text } from '@/components/IncomeCard/TrustedSources/styles'
import { TrustedSourceCard } from '@/components/IncomeCard/TrustedSources/TrustedSourceCard'
import { getOpenBankingOptOutReasonText } from '@/components/IncomeCard/utils'
import type { IncomeType, OpenBankingConnection } from '@/types/Activity'
import type { IncomeProperties } from '@/types/Income'

import OpenBankingOutcome from './OpenBankingOutcome'
import ViewReportLink from './ViewReportLink'

const FAQS = [
  {
    question: 'Why is a 2-month OB connection different from 3 months or more?',
    answer:
      'If an applicant has been employed for 2 months then you might see 1 or 2 income payments in the OB report. This is not the case for 3-month connections. This means you have to be more careful when checking these connections',
  },
  {
    question:
      'Do I need to chase for an employment contract if I have an OB connection for 2 months employed?',
    answer:
      "No, you don't need to chase for employment contract. You can verify the income based on OB.",
  },
]

const GUIDE_ME_INTRO = (
  <>
    Here&apos;s how to assess an applicant who has provided Open Banking for an employment
    that is 2-3 months.
    <ul style={{ marginTop: '40px' }}>
      <li style={{ marginTop: '8px' }}>
        If you see 1 payment in OB then take that figure as monthly income and calculate
        the affordability
      </li>
      <li style={{ marginTop: '8px' }}>
        If you see 2 payments in the OB connection
        <ul>
          <li>Both equal - Verify based on average</li>
          <li>
            First is less, the second is more - Take the second payment and verify the
            income
          </li>
        </ul>
      </li>
    </ul>
  </>
)

export interface Props {
  incomeProperties: IncomeProperties
  incomeType: IncomeType
  isShortEmployment?: boolean
  openBanking: OpenBankingConnection
  hasSingleIncome: boolean
  customerId: string
  subjectId: string
}

const OpenBankingDetails: React.FC<Props> = ({
  incomeProperties,
  incomeType,
  isShortEmployment,
  openBanking,
  hasSingleIncome,
  customerId,
  subjectId,
}) => {
  const isOpenBankingConnected = !!openBanking?.outcome

  const hasOutcome = !!openBanking?.outcome
  const connectionFailures = (openBanking.connectionFailures ?? [])
    .slice()
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

  const completedAtLabel = openBanking.completedAt
    ? format(new Date(openBanking.completedAt), 'dd MMM yyyy, HH:mm')
    : null

  return (
    <TrustedSourceCard
      icon={faBuildingColumns}
      title="Open Banking"
      isConnected={isOpenBankingConnected}
      isShortEmployment={isShortEmployment && incomeType !== 'Savings'}
      guideMe={{
        sidebarTitle: 'Open Banking Guide Me',
        intro: GUIDE_ME_INTRO,
        faqs: FAQS,
        bannerCopy: "Note: This applicant's current employment is between 2-3 months.",
      }}
      statusMessage={
        isOpenBankingConnected
          ? 'An open banking application has been retrieved for this application.'
          : 'The applicant was eligible for Open Banking but did not connect their bank account.'
      }
      hasOutcome={hasOutcome}
      comments={openBanking.outcome?.comments}
      connectionFailures={connectionFailures}
      optOutReason={openBanking.optOutReason}
      getOptOutReasonText={getOpenBankingOptOutReasonText}
      showAdditionalInformationOnlyFor="somethingElse"
      headerContent={
        (completedAtLabel || openBanking.source === 'Request') && (
          <Text variant="body2">
            {completedAtLabel}
            {openBanking.source === 'Request' &&
              (completedAtLabel
                ? ' · reconnected after chaser'
                : 'Reconnected after chaser')}
          </Text>
        )
      }
      outcomeContent={
        openBanking.outcome && (
          <OpenBankingOutcome
            outcome={openBanking.outcome}
            incomeProperties={incomeProperties}
            incomeType={incomeType}
            hasSingleIncome={hasSingleIncome}
          />
        )
      }
      actionLink={
        openBanking.reportUrl && (
          <ViewReportLink
            reportUrl={openBanking.reportUrl}
            customerId={customerId}
            subjectId={subjectId}
          />
        )
      }
    />
  )
}

export default OpenBankingDetails
